import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Inbox, FileText, MessageSquare, Bell, ExternalLink } from "lucide-react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Logo";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("cd_token", data.token);
      onLogin(data.user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login" className="bg-white p-8 md:p-10 w-full max-w-md border-t-4 border-orange">
        <Logo />
        <h1 className="font-display font-bold text-2xl mt-8">Admin Dashboard</h1>
        <p className="text-sm text-text-secondary mt-1">Sign in to view freight leads.</p>
        <div className="mt-8 space-y-4">
          <input data-testid="login-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                 className="w-full border border-border px-4 py-3 focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/30 rounded-none" />
          <input data-testid="login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                 className="w-full border border-border px-4 py-3 focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/30 rounded-none" />
          <button data-testid="login-submit" disabled={loading} className="btn-orange w-full py-3.5 font-semibold uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-border p-6">
    <div className="flex items-center justify-between">
      <span className="tag-eyebrow">{label}</span>
      <Icon size={20} className="text-orange" strokeWidth={1.5} />
    </div>
    <p className="font-display font-black text-4xl mt-3 text-navy">{value}</p>
  </div>
);

function Dashboard({ user, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, quotes: 0, contacts: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/stats")]);
      setLeads(l.data); setStats(s.data);
    } catch {
      toast.error("Session expired. Please sign in again.");
      onLogout();
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (lead) => {
    const next = lead.status === "new" ? "contacted" : "new";
    await api.patch(`/admin/leads/${lead.id}`, { status: next });
    load();
    if (selected?.id === lead.id) setSelected({ ...lead, status: next });
  };

  const openFile = async (f) => {
    try {
      const res = await api.get(`/files/${f.storage_path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open file"); }
  };

  return (
    <div className="min-h-screen bg-offwhite">
      <header className="bg-navy text-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Logo light />
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/60 hidden sm:block">{user.email}</span>
            <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-2 text-sm font-semibold hover:text-orange transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-10">
        <h1 className="font-display font-bold text-3xl">Freight Leads</h1>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mt-6">
          <StatCard icon={Inbox} label="Total Leads" value={stats.total} />
          <StatCard icon={Bell} label="New" value={stats.new} />
          <StatCard icon={FileText} label="Quote Requests" value={stats.quotes} />
          <StatCard icon={MessageSquare} label="Contact Messages" value={stats.contacts} />
        </div>

        <div className="bg-white border border-border mt-8 overflow-x-auto" data-testid="leads-table">
          {loading ? (
            <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-orange" /></div>
          ) : leads.length === 0 ? (
            <div className="p-16 text-center text-text-secondary">No leads yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-navy text-white text-left">
                <tr>
                  {["Name", "Type", "Company", "Service", "Received", "Status", ""].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-border hover:bg-offwhite">
                    <td className="px-4 py-3 font-semibold">{l.full_name}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-mono uppercase ${l.lead_type === "quote" ? "bg-orange/15 text-orange" : "bg-navy/10 text-navy"}`}>{l.lead_type}</span></td>
                    <td className="px-4 py-3 text-text-secondary">{l.company_name || "—"}</td>
                    <td className="px-4 py-3 text-text-secondary">{l.service_required || "—"}</td>
                    <td className="px-4 py-3 text-text-secondary whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleStatus(l)} data-testid={`status-${l.id}`}
                              className={`px-2.5 py-1 text-xs font-semibold uppercase ${l.status === "new" ? "bg-orange text-white" : "bg-gray-200 text-charcoal"}`}>
                        {l.status}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelected(l)} className="text-orange font-semibold hover:underline">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-navy/60 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto border-t-4 border-orange" onClick={(e) => e.stopPropagation()} data-testid="lead-modal">
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-start">
                <div>
                  <span className="tag-eyebrow">{selected.lead_type} request</span>
                  <h2 className="font-display font-bold text-2xl mt-1">{selected.full_name}</h2>
                </div>
                <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-orange text-2xl leading-none">×</button>
              </div>
              <dl className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[
                  ["Company", selected.company_name], ["Email", selected.email], ["Phone", selected.phone],
                  ["Service", selected.service_required], ["Pickup", selected.pickup_location], ["Delivery", selected.delivery_location],
                  ["Freight Type", selected.freight_type], ["Pallets", selected.num_pallets], ["Weight", selected.total_weight],
                  ["Dimensions", selected.freight_dimensions], ["Arrival", selected.expected_arrival_date], ["Storage", selected.storage_duration],
                  ["Preferred Contact", selected.preferred_contact],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}><dt className="text-gray-400 uppercase text-xs tracking-wide">{k}</dt><dd className="font-medium">{v}</dd></div>
                ))}
              </dl>
              {(selected.message || selected.special_instructions || selected.additional_details) && (
                <div className="mt-5 space-y-3">
                  {[["Message", selected.message], ["Special Instructions", selected.special_instructions], ["Additional Details", selected.additional_details]]
                    .filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="bg-offwhite p-4"><p className="text-xs text-gray-400 uppercase tracking-wide">{k}</p><p className="mt-1">{v}</p></div>
                    ))}
                </div>
              )}
              {selected.files?.length > 0 && (
                <div className="mt-5">
                  <p className="tag-eyebrow mb-2">Attachments</p>
                  <ul className="space-y-2">
                    {selected.files.map((f) => (
                      <li key={f.id}>
                        <button onClick={() => openFile(f)} className="flex items-center gap-2 text-orange font-semibold hover:underline text-sm">
                          <ExternalLink size={15} /> {f.original_filename}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-6 flex gap-3">
                <a href={`mailto:${selected.email}`} className="btn-orange px-5 py-2.5 text-sm font-semibold uppercase tracking-wide">Reply by Email</a>
                <button onClick={() => toggleStatus(selected)} className="px-5 py-2.5 text-sm font-semibold uppercase tracking-wide border-2 border-navy hover:bg-navy hover:text-white transition-colors">
                  Mark {selected.status === "new" ? "Contacted" : "New"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("cd_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => localStorage.removeItem("cd_token")).finally(() => setChecking(false));
  }, []);

  const logout = () => { api.post("/auth/logout").catch(() => {}); localStorage.removeItem("cd_token"); setUser(null); };

  if (checking) return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="animate-spin text-orange" size={32} /></div>;
  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
